/**************
 Cart State
 ***************/

// escape merchant/customer-supplied text before it goes into a template string
function escapeHTML(value) {
    return String(value)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
}

// greatest compare-at price for a line item - there are 3 sources (selling plan allocation,
// line item discount, product compare_at_price); returns null when none apply
function itemComparePrice(item, supplement) {
    const comparePrices = [];

    if (item.selling_plan_allocation) {
        comparePrices.push(item.selling_plan_allocation.compare_at_price * item.quantity);
    }

    if (item.original_line_price > item.final_line_price) {
        comparePrices.push(item.original_line_price);
    }

    if (supplement && supplement.compare_at_price) {
        comparePrices.push(supplement.compare_at_price * item.quantity);
    }

    if (comparePrices.length === 0) {
        return null;
    }

    return Math.max(...comparePrices);
}

/**************
 Open Cart
 ***************/

// open cart
// A/B (Shoplift) exposure latch: true once cart:opened has fired for the drawer's current
// visit, reset when it closes. openCart() doubles as the re-render after every cart mutation, so
// a latch is what keeps one viewing from dispatching on every quantity tap. Removal marker:
// cart:opened - see TASKS.md.
let cartExposureDispatched = false;

async function openCart(skipFetch = false) {
    // open cart if not already open
    if (document.querySelector("#miniCart:not([open])")) {
        document.querySelector("#miniCart").showModal();

        // move initial focus to the dialog
        document.querySelector("#miniCart").focus({preventScroll: true});
    }
    document.querySelector("body").classList.add("noScroll");

    // un-hide the auto-hiding header so the shared backdrop (a fixed child of #header) isn't dragged off-screen by the header's translateY transform - scroll is locked while the cart is open, so it stays put
    document.querySelector("#header")?.classList.remove("scrolledDown");

    // get updated variables
    const [freshCart, freshData] = await Promise.all([getCart(), getCartData()]);

    // update globals
    cart = freshCart;
    cartData = freshData;

    // Split-theme test attribution: re-stamp if this cart was built on the other theme.
    // The drawer is the ONLY cart on this store (/cart redirects to the homepage) and there are
    // no dynamic/express checkout buttons anywhere, so opening it is the last guaranteed moment
    // before checkout — which makes this the right place to catch a cart that crossed cohorts.
    //
    // Deliberately not awaited: the attribute has no bearing on what the drawer renders, so
    // blocking the open on a network round-trip would add latency for zero visible benefit. The
    // .catch keeps a failed stamp from surfacing as an unhandled rejection in production.
    reconcileThemeTestAttribute(cart).catch(() => {});

    // let sitewide listeners (e.g. the PDP free-shipping text) react to the new cart state
    document.dispatchEvent(new CustomEvent("cart:updated"));

    // hide empty cart section
    document.querySelector("#miniCartEmpty").classList.remove("active");

    // update cart count in header nav
    document.querySelector("#headerIconsCart").setAttribute("data-count", cart.item_count);

    // announce cart state to screen readers via the persistent live region
    const miniCartStatus = document.querySelector("#miniCartStatus");
    if (miniCartStatus) {
        miniCartStatus.textContent = cart.item_count === 0
            ? "Your cart is empty"
            : `${cart.item_count} ${cart.item_count === 1 ? "item" : "items"} in cart, total ${price(qualifyingTotal())}`;
    }

    // free delivery/gift tracker
    renderTracker();

    // check for empty cart and exit
    if (cart.item_count === 0) {
        document.querySelector("#miniCartEmpty").classList.add("active");
        document.querySelector("#miniCartBody").classList.remove("active");
        document.querySelector("#miniCartFooter").classList.remove("active");
        renderEmptyRecommendations();
        return;
    }

    // show main cart sections
    document.querySelector("#miniCartBody").classList.add("active");
    document.querySelector("#miniCartFooter").classList.add("active");

    // A/B (Shoplift) exposure signal: the shopper is now looking at a cart that HAS a footer.
    // It sits past the empty-cart return above rather than at the top of openCart(), which is
    // what makes it survive the awkward path - open an empty drawer, add something from the
    // empty-cart recommendations without ever closing it - where the footer appears without a
    // fresh open. Gating on "was the drawer closed" instead would silently skip those shoppers.
    // This matters on a MANUALLY triggered test, where the listener's isHypothesisActive() call
    // IS the enrolment: enrolling empty-cart opens would count shoppers who never saw the variant
    // and bury real lift, while missing these would quietly shrink the sample. Dispatched before
    // renderFooter() so the gate's network round-trip overlaps the render instead of following it.
    if (!cartExposureDispatched) {
        cartExposureDispatched = true;
        document.dispatchEvent(new CustomEvent("cart:opened"));
    }

    // line items, upsell row, footer totals
    renderItems();
    renderUpsells();
    renderFooter();

    // stop loading animation
    document.querySelector("#miniCartContent").classList.remove("loading");

    // add/remove the free gift to match the milestone thresholds against the current cart
    await reconcileFreeGift();
}

/**************
 Tracker
 ***************/

// free delivery/gift tracker in the cart header
function renderTracker() {
    const trackerDots = document.querySelectorAll(".miniCartHeaderTrackerBarDot");
    if (trackerDots.length === 0) {
        return;
    }

    // what the customer pays, not the pre-discount line sum — see qualifyingTotal()
    const subtotal = qualifyingTotal();

    document.querySelectorAll(".miniCartHeaderTrackerListItem").forEach(item => {
        const threshold = parseInt(item.dataset.threshold);
        const textEl = item.querySelector(".miniCartHeaderTrackerListItemText");
        // toggle `.reached` on each dot if the order meets its threshold
        if (subtotal >= threshold) {
            textEl.textContent = item.dataset.qualifiedText;
            item.classList.add("reached");
        } else {
            // replace {amount} with the remaining amount needed to qualify
            textEl.textContent = item.dataset.unqualifiedText.replace("{amount}", price(threshold - subtotal));
            item.classList.remove("reached");
        }
    });

    // fill width - dot positions are threshold/maxThreshold (last dot = 100%);
    // snap to each reached dot, then interpolate linearly toward the next one
    const maxThreshold = Math.max(...Array.from(trackerDots).map(d => parseInt(d.dataset.threshold)));
    let fillPercent = 0;
    let interpolated = false;
    for (let i = 0; i < trackerDots.length; i++) {
        const threshold = parseInt(trackerDots[i].dataset.threshold);
        const dotPosition = (threshold / maxThreshold) * 100;
        if (subtotal >= threshold) {
            trackerDots[i].classList.add("reached");
            fillPercent = dotPosition;
        } else {
            trackerDots[i].classList.remove("reached");
            if (!interpolated) {
                interpolated = true;
                const prevThreshold = i === 0 ? 0 : parseInt(trackerDots[i - 1].dataset.threshold);
                const prevPosition = i === 0 ? 0 : (prevThreshold / maxThreshold) * 100;
                const span = threshold - prevThreshold;
                if (span > 0) {
                    const progress = Math.max(0, (subtotal - prevThreshold) / span);
                    fillPercent = prevPosition + (dotPosition - prevPosition) * progress;
                }
            }
        }
    }
    document.querySelector("#miniCartHeaderTrackerBarFill").style.width = `${fillPercent}%`;
}

// add/remove the free-gift line so the cart matches the gift milestone.
// the subtotal excludes the gift itself, so adding it can't re-trigger its own removal.
async function reconcileFreeGift() {
    const milestone = document.querySelector(".miniCartHeaderTrackerListItem[data-gift-variant-id]");
    if (!milestone) {
        return false;
    }

    const threshold = parseInt(milestone.dataset.threshold);
    const variantId = parseInt(milestone.dataset.giftVariantId);

    // what the customer pays, gift excluded so it never counts toward its own threshold
    const subtotal = qualifyingTotal();

    const giftLine = cart.items.find(item => item.properties?._free_gift);
    const qualifies = subtotal >= threshold;

    // already in the right state - nothing to do
    if (qualifies === !!giftLine) {
        return false;
    }

    // move above threshold -> add the gift; drop below -> remove it
    if (qualifies) {
        await addProduct({items: [{id: variantId, quantity: 1, properties: {_free_gift: "true"}}]});
        cart = await getCart();
    } else {
        cart = await updateCart({updates: {[giftLine.key]: 0}});
    }
    return true;
}

/**************
 Line Items
 ***************/

// render the cart line items and wire up their quantity/remove/upgrade controls
function renderItems() {
    // reset body container
    document.querySelector("#miniCartBodyItems").innerHTML = "";

    // grab the remove-icon template once per render
    const removeIconTemplate = document.querySelector("#miniCartRemoveIconTemplate");

    // append cart
    cart.items.forEach(function (item) {
        // save supplement data
        const supplementData = cartData[item.key];

        // determine if item has properties
        const hasProperties = item.properties && Object.keys(item.properties).length > 0;

        // free gift lines are auto-managed, so they drop the quantity stepper and remove button
        const isGift = !!(item.properties && item.properties._free_gift);

        // a personalization add-on fee is a linked line item, not an independently
        // manageable one - it has no controls of its own; updateCartItem() below keeps its
        // quantity/removal in sync with whichever personalized line it's linked to instead
        const isPersonalizeFee = !!(item.properties && item.properties._personalize_fee);

        // variant HTML - render each option on its own line as "Name: Value"
        function variantHTML() {
            if (item.variant_title === null || !item.options_with_values) {
                return "";
            }
            let optionsHTML = "";
            item.options_with_values.forEach(opt => {
                optionsHTML += `<span class="miniCartBodyItemsItemInfoVariantOption">${opt.name}: ${opt.value}</span>`;
            });
            return `<div class="miniCartBodyItemsItemInfoVariant">${optionsHTML}</div>`;
        }

        // A/B (Shoplift): warranty reassurance chip under the variant options. SHIPS OFF —
        // renders nothing unless the visitor is in the assigned variant cohort, which
        // layout/theme.liquid signals with the warrantyTooltipOn class on <html>. Control sees
        // the cart exactly as it is today. Skipped on free gift and personalization-fee lines,
        // which aren't warrantied products of their own.
        function warrantyHTML() {
            const text = cartSettings.warranty_callout_text;
            const active = document.documentElement.classList.contains("warrantyTooltipOn");
            if (!active || !text || isGift || isPersonalizeFee) {
                return "";
            }

            // the tooltip body is a richtext setting, so Shopify has already constrained it to a
            // safe tag subset - it goes in as markup on purpose, unlike the escaped label below
            const info = cartSettings.warranty_callout_info;
            const infoHTML = info
                ? `<button class="miniCartBodyItemsItemInfoWarrantyInfo" type="button" aria-label="More info about the warranty" aria-expanded="false">
                        <svg viewBox="0 0 16 16" width="12" height="12" aria-hidden="true" focusable="false">
                            <path fill="currentColor" d="M8 .833A7.167 7.167 0 1 0 8 15.167 7.167 7.167 0 0 0 8 .833Zm0 13.334A6.167 6.167 0 1 1 8 1.833a6.167 6.167 0 0 1 0 12.334ZM7.25 7.25h1.5v4.583h-1.5V7.25Zm0-3h1.5v1.5h-1.5v-1.5Z"/>
                        </svg>
                    </button>
                    <div class="miniCartBodyItemsItemInfoWarrantyPopover">${info}</div>`
                : "";

            return `
                <div class="miniCartBodyItemsItemInfoWarranty">
                    <svg class="miniCartBodyItemsItemInfoWarrantyIcon" viewBox="0 0 16 16" width="12" height="12" fill="none" aria-hidden="true" focusable="false">
                        <path d="M8 1.5 2.5 3.7v4.1c0 3.2 2.2 5.4 5.5 6.7 3.3-1.3 5.5-3.5 5.5-6.7V3.7L8 1.5Z" stroke="currentColor" stroke-width="1.4" stroke-linejoin="round"/>
                        <path d="m5.9 7.9 1.5 1.5 2.9-2.9" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                    <span>${escapeHTML(text)}</span>
                    ${infoHTML}
                </div>
            `;
        }

        // property HTML
        function propertiesHTML() {
            if (!hasProperties) {
                return "";
            }

            let propertiesInner = "";
            for (const key in item.properties) {
                // skip private properties
                if (key.startsWith("_")) {
                    continue;
                }
                if (typeof item.properties[key] === "string" && item.properties[key].includes("https://")) {
                    propertiesInner += `<a href="${item.properties[key]}" target="_blank">${key}</a><br>`;
                    continue;
                }
                propertiesInner += `${key}: ${item.properties[key]}<br>`;
            }

            if (propertiesInner === "") {
                return "";
            }

            return `<div class="miniCartBodyItemsItemInfoProperties">${propertiesInner}</div>`;
        }

        // subscription HTML
        function subscriptionHTML() {
            if (item.selling_plan_allocation) {
                return `<div class="miniCartBodyItemsItemInfoSellingPlan">${item.selling_plan_allocation.selling_plan.name}</div>`;
            }
            return "";
        }

        // price HTML - current price + (optional) compare-at price
        function priceHTML() {
            // free gift - shown as free, with pre-discount price struck through
            if (item.properties && item.properties._free_gift) {
                return `<span class="miniCartBodyItemsItemPriceCurrent">Free</span><span class="miniCartBodyItemsItemPriceCompare">${price(item.original_line_price)}</span>`;
            }

            let HTML = `<span class="miniCartBodyItemsItemPriceCurrent">${price(item.final_line_price)}</span>`;

            const comparePrice = itemComparePrice(item, supplementData);
            if (comparePrice && comparePrice > item.final_line_price) {
                HTML += `<span class="miniCartBodyItemsItemPriceCompare">${price(comparePrice)}</span>`;
            }

            return HTML;
        }

        // upgrade button HTML
        function upgradeHTML() {
            if (item.selling_plan_allocation) {
                return "";
            }
            if (!supplementData || !supplementData["selling_plan_id"]) {
                return "";
            }

            return `
                <button
                    class="miniCartBodyItemsItemUpgrade button"
                    data-key="${item.key}"
                    data-quantity="${item.quantity}"
                    data-plan="${supplementData["selling_plan_id"]}"
                >
                    ${supplementData["selling_plan_name"]}
                </button>
            `;
        }

        let quantityHTML = `
                    <div class="miniCartBodyItemsItemInfoQuantity">
                        <button class="miniCartBodyItemsItemInfoQuantityMinus" type="button" aria-label="Decrease quantity">−</button>
                        <span class="miniCartBodyItemsItemInfoQuantityCount">${item.quantity}</span>
                        <button class="miniCartBodyItemsItemInfoQuantityPlus" type="button" aria-label="Increase quantity">+</button>
                    </div>`;
        let removeHTML = `<button class="miniCartBodyItemsItemRemove" type="button" aria-label="Remove item"></button>`;
        let giftAttr = "";
        if (isGift) {
            quantityHTML = "";
            removeHTML = "";
            giftAttr = ` data-free-gift="true"`;
        }
        if (isPersonalizeFee) {
            quantityHTML = "";
            removeHTML = "";
            giftAttr = ` data-personalize-fee="true"`;
        }

        // bundled lines can't change quantity (it would break the bundle's pricing), so swap the
        // stepper for a static "Bundled" chip; the remove button stays so removing still un-bundles
        if (item.properties && item.properties._bundle) {
            quantityHTML = `<div class="miniCartBodyItemsItemInfoBundle">Bundled</div>`;
        }

        // personalized items carry a rendered on-product mockup (Cloudinary URL) that replaces
        // the default variant image in the cart; it's already sized for this thumbnail, so skip
        // Shopify's own CDN "&width=" param, which Cloudinary doesn't understand
        const thumbSrc = (item.properties && item.properties._preview_image) || `${item.image}&width=200`;

        // insert item
        document.querySelector("#miniCartBodyItems").insertAdjacentHTML("beforeend", `
            <div class="miniCartBodyItemsItem" data-key="${item.key}"${giftAttr}>
                <img class="miniCartBodyItemsItemImage" src="${thumbSrc}" alt="${item.product_title}">
                <div class="miniCartBodyItemsItemInfo">
                    <div>
                        <div class="miniCartBodyItemsItemInfoTitle">${item.product_title}</div>
                        ${variantHTML()}
                        ${warrantyHTML()}
                        ${propertiesHTML()}
                        ${subscriptionHTML()}
                    </div>
                    ${quantityHTML}
                </div>
                <div class="miniCartBodyItemsItemPrice">${priceHTML()}</div>
                ${removeHTML}
                ${upgradeHTML()}
            </div>
        `);

        // inject the remove icon from the template (avoids re-stringifying it on every render)
        const lastItem = document.querySelector("#miniCartBodyItems").lastElementChild;
        const removeButton = lastItem.querySelector(".miniCartBodyItemsItemRemove");
        if (removeIconTemplate && removeButton) {
            removeButton.appendChild(removeIconTemplate.content.cloneNode(true));
        }
    });

    // event listener for each line item
    document.querySelectorAll(".miniCartBodyItemsItem").forEach(function (element) {
        // free gift and personalization-fee lines have no quantity/remove controls to wire
        if (element.dataset.freeGift || element.dataset.personalizeFee) {
            return;
        }

        const key = element.dataset.key;

        // bundled lines render a chip instead of the stepper, so the count may be absent
        const countEl = element.querySelector(".miniCartBodyItemsItemInfoQuantityCount");
        let quantity = countEl ? parseInt(countEl.textContent) : 0;

        // minus button - decrement, or remove entirely at quantity 1 (absent on bundled lines)
        element.querySelector(".miniCartBodyItemsItemInfoQuantityMinus")?.addEventListener("click", function () {
            if (quantity === 1) {
                element.querySelector(".miniCartBodyItemsItemRemove").click();
            } else {
                quantity--;
                updateCartItem();
            }
        });

        // plus button (absent on bundled lines)
        element.querySelector(".miniCartBodyItemsItemInfoQuantityPlus")?.addEventListener("click", function () {
            quantity++;
            updateCartItem();
        });

        // remove button
        element.querySelector(".miniCartBodyItemsItemRemove").addEventListener("click", function () {
            quantity = 0;
            updateCartItem();
        });

        // push the new quantity to Shopify, then re-render from the fresh cart
        async function updateCartItem() {
            if (countEl) {
                countEl.textContent = quantity;
            }
            document.querySelector("#miniCartContent").classList.add("loading");

            // fully removing a line breaks its bundle, so grab its bundle id
            let removedBundleID = null;
            if (quantity === 0) {
                const removedItem = cart.items.find(item => item.key === key);
                removedBundleID = removedItem?.properties?._bundle_id || null;
            }

            // removing a bundled line also un-bundles its siblings (their discount no longer applies).
            if (removedBundleID) {
                await updateCart({updates: {[key]: quantity}});
                const siblings = cart.items.filter(item => item.properties?._bundle_id === removedBundleID);
                for (const sibling of siblings) {
                    await changeCart({id: sibling.key, properties: {_bundle_id: removedBundleID}});
                }
                await openCart();
                return;
            }

            // changing (or fully removing) a personalized line keeps its linked add-on fee
            // line's quantity in sync, so the fee can never outlive its item or under/over-
            // count it - quantity 0 here removes the fee line the same way it removes this one.
            // Batched into one /cart/update.js call (rather than two sequential ones) so the
            // pair updates atomically from the client's perspective - a failure between two
            // separate calls would otherwise leave the fee desynced from its parent's quantity.
            const personalizeLinkID = cart.items.find(item => item.key === key)?.properties?._personalize_link_id || null;
            if (personalizeLinkID) {
                const linkedFee = cart.items.find(item => item.key !== key && item.properties?._personalize_link_id === personalizeLinkID);
                const updates = {[key]: quantity};
                if (linkedFee) {
                    updates[linkedFee.key] = quantity;
                }
                await updateCart({updates});
                await openCart();
                return;
            }

            // plain quantity change: update the line and render from the response
            await updateCart({updates: {[key]: quantity}});
            await openCart();
        }

        // upgrade button - swap the item onto its subscription selling plan
        element.querySelector(".miniCartBodyItemsItemUpgrade")?.addEventListener("click", async function () {
            const upgradeKey = this.dataset.key;
            const upgradeQuantity = parseInt(this.dataset.quantity);
            const sellingPlanID = parseInt(this.dataset.plan);

            document.querySelector("#miniCartContent").classList.add("loading");

            cart = await changeCart({
                id: upgradeKey,
                quantity: upgradeQuantity,
                selling_plan: sellingPlanID
            });

            await openCart();
            this.classList.remove("loading");
        });
    });
}

/**************
 Upsells
 ***************/

// hide upsell cards already in the cart, then cap the visible count so additions
// pull the next pick into view instead of shrinking the row
function renderUpsells() {
    // hide cards for products already in the cart
    document.querySelectorAll(".miniCartBodyUpsellsScrollItem").forEach(element => {
        const productID = parseInt(element.dataset.product);
        const isInCart = cart.items.find(item => item.product_id === productID);
        element.classList.toggle("hidden", !!isInCart);
    });

    // cap the visible count
    const maxVisibleUpsells = parseInt(cartSettings.max_visible_upsells) || 3;
    let visibleUpsellCount = 0;
    document.querySelectorAll(".miniCartBodyUpsellsScrollItem").forEach(element => {
        if (element.classList.contains("hidden")) {
            return;
        }
        if (visibleUpsellCount >= maxVisibleUpsells) {
            element.classList.add("hidden");
        } else {
            visibleUpsellCount++;
        }
    });

    // hide the whole block when nothing is left to show
    const upsellsBlock = document.querySelector("#miniCartBodyUpsells");
    if (upsellsBlock) {
        upsellsBlock.style.display = visibleUpsellCount === 0 ? "none" : "";
    }
}

/**************
 Empty-cart recommendations
 ***************/

// Populates the empty cart with a collection's products as add-to-cart cards, in the
// collection's own sort order. The cards come from the theme-mini-cart-recommendations
// section via the Section Rendering API rather than being built here from products.json:
// they use the same collection-item card as the cart upsells, whose swatches need option
// swatch colours/images and variant metafields that the Ajax endpoints don't return.
// Requesting the section at /collections/<handle> is what gives it collection context, so
// the sort order survives — which a layout-rendered loop would have lost.
// Fetched once, lazily, the first time an empty cart is opened.
// 'idle' → 'loading' → 'done'; left at 'idle' on failure so the next open can retry.
let emptyRecsState = "idle";

async function renderEmptyRecommendations() {
    const container = document.querySelector("#miniCartEmptyRecommendations");
    // feature off (no collection set → the Liquid block isn't rendered), already building, or already built
    if (!container || emptyRecsState === "loading" || emptyRecsState === "done") {
        return;
    }

    const handle = container.dataset.collection;
    const count = parseInt(container.dataset.count) || 8;
    if (!handle) {
        return;
    }

    emptyRecsState = "loading";
    const list = container.querySelector("#miniCartEmptyRecommendationsList");

    let cards;
    try {
        const response = await fetch(`${Shopify.routes.root}collections/${handle}?section_id=theme-mini-cart-recommendations`);
        if (!response.ok) {
            throw new Error(`section render ${response.status}`);
        }
        // parsed into an inert document, so trimming to the configured count happens before
        // anything is live: the cards we drop never request their images. Setting innerHTML
        // first and removing the extras afterwards would fetch every thumbnail we discard.
        const parsed = new DOMParser().parseFromString(await response.text(), "text/html");
        cards = Array.from(parsed.querySelectorAll(".collectionItem")).slice(0, count);
    } catch (e) {
        // leave the block hidden — the empty cart falls back to just its title
        emptyRecsState = "idle";
        return;
    }

    if (cards.length === 0) {
        emptyRecsState = "idle";
        return;
    }

    // the section's own wrapper is left behind — the list supplies the row layout.
    // Cards carry the sitewide .collectionItemAdd button, handled by theme-global.js
    cards.forEach(card => list.appendChild(document.importNode(card, true)));
    list.setAttribute("aria-busy", "false");
    container.hidden = false;
    document.querySelector("#miniCartEmpty").classList.add("hasRecommendations");
    emptyRecsState = "done";
}

/**************
 Footer Totals
 ***************/

// footer subtotal, savings line, and checkout price
function renderFooter() {
    // subtotal is the cart's full compare-at value; savings is the gap between that and
    // what the customer pays, so compare-at markdowns, line-level (bundle) discounts and
    // cart-level codes are each counted exactly once - never derive savings by re-adding
    // cart.total_discount, it already lives inside final_line_price for line-level discounts
    let preDiscountTotal = 0;
    let giftTotal = 0;
    cart.items.forEach(item => {
        // managed free gifts are shown at no cost, so keep their price out of the displayed totals
        if (item.properties && item.properties._free_gift) {
            giftTotal += item.final_line_price;
            return;
        }
        const comparePrice = itemComparePrice(item, cartData[item.key]);
        preDiscountTotal += Math.max(item.final_line_price, comparePrice || 0);
    });

    // what the customer actually pays (the gift's price is excluded to match the
    // discount that zeroes it at checkout) — shared with the tracker so they cannot drift
    const payableTotal = qualifyingTotal();
    const totalSavings = preDiscountTotal - payableTotal;

    // footer totals - subtotal is pre-discount so savings show as a separate line below
    document.querySelector("#miniCartFooterSummarySubtotalAmount").textContent = price(preDiscountTotal);
    document.querySelector("#miniCartFooterCheckoutPrice").textContent = price(payableTotal);

    // savings pill + amount line, only when there is something saved
    const discountsRow = document.querySelector("#miniCartFooterSummaryDiscounts");
    if (totalSavings > 0) {
        // same rounding as the PDP savings badge: nearest whole percent, then UP to the next multiple of 5
        const rawPercent = Math.round((totalSavings / preDiscountTotal) * 100);
        const discountPercent = Math.ceil(rawPercent / 5) * 5;
        document.querySelector("#miniCartFooterSummaryDiscountsLeftPill").textContent = `SAVING ${discountPercent}%`;
        document.querySelector("#miniCartFooterSummaryDiscountsAmount").textContent = `-${price(totalSavings)}`;
        discountsRow.classList.add("active");
    } else {
        discountsRow.classList.remove("active");
    }

    // A/B (Shoplift) shipping line - free once the cart clears the free-shipping milestone,
    // otherwise deferred to checkout. Runs for BOTH cohorts: the row is hidden by CSS in control,
    // so keeping the text current costs nothing and means the variant never paints a stale value
    // in the instant the gate class lands. Reads qualifyingTotal(), the same figure the header
    // tracker and the PDP shipping line use, so the drawer cannot promise free shipping in the
    // tracker and withhold it three rows below. No milestones configured -> null threshold -> the
    // row stays on "calculated at checkout", which is the safe claim. Taxes is static, no JS.
    const shippingRow = document.querySelector("#miniCartFooterSummaryShipping");
    if (shippingRow) {
        const threshold = freeShippingThreshold();
        const shippingIsFree = threshold !== null && qualifyingTotal() >= threshold;
        document.querySelector("#miniCartFooterSummaryShippingAmount").textContent = shippingIsFree
            ? shippingRow.dataset.freeText
            : shippingRow.dataset.calculatedText;
        shippingRow.classList.toggle("isFree", shippingIsFree);
    }
}

/**************
 Warranty Tooltip (A/B variant only)
 ***************/

// Info popover on the warranty callout, mirroring the PDP info badges
// (productV1ContentInfoBadgesItemInfo in main-product-v1.js). Delegated from the static
// #miniCartBodyItems container because renderItems() replaces its innerHTML on every cart
// change, which would drop any per-button listener. Inert in the control cohort, where
// warrantyHTML() renders nothing for these selectors to match.

// breathing room kept between the popover and the drawer's inner edges
const MINI_CART_POPOVER_GUTTER = 8;

function closeWarrantyPopovers() {
    document.querySelectorAll(".miniCartBodyItemsItemInfoWarrantyPopover.active").forEach(function (popover) {
        popover.classList.remove("active");
        popover.classList.remove("flipUp");
        popover.style.left = "";
        popover.parentElement.querySelector(".miniCartBodyItemsItemInfoWarrantyInfo").setAttribute("aria-expanded", "false");
    });
}

document.querySelector("#miniCartBodyItems").addEventListener("click", function (e) {
    const infoBtn = e.target.closest(".miniCartBodyItemsItemInfoWarrantyInfo");
    if (!infoBtn) {
        return;
    }

    const popover = infoBtn.parentElement.querySelector(".miniCartBodyItemsItemInfoWarrantyPopover");
    const willOpen = !popover.classList.contains("active");

    // only one open at a time, same as the PDP badges
    closeWarrantyPopovers();

    if (!willOpen) {
        return;
    }

    popover.classList.add("active");
    infoBtn.setAttribute("aria-expanded", "true");

    // #miniCartBody is overflow-y: auto (which makes overflow-x auto too), so the popover is
    // clipped on both axes. Clamp it to the drawer rather than to the viewport: the drawer is
    // what actually bounds it, and a vw-based max-width would guess wrong at every breakpoint.
    const bodyRect = document.querySelector("#miniCartBody").getBoundingClientRect();
    const rect = popover.getBoundingClientRect();

    if (rect.bottom > bodyRect.bottom) {
        popover.classList.add("flipUp");
    }

    const overflowRight = rect.right - (bodyRect.right - MINI_CART_POPOVER_GUTTER);
    if (overflowRight > 0) {
        // left is 0 by default, so the shift is just the negative overflow
        popover.style.left = `${Math.max(-rect.left + bodyRect.left + MINI_CART_POPOVER_GUTTER, -overflowRight)}px`;
    }
});

// close on outside click and on Escape (Escape must not also close the cart dialog)
document.addEventListener("mousedown", function (e) {
    if (e.target.closest(".miniCartBodyItemsItemInfoWarranty")) {
        return;
    }
    closeWarrantyPopovers();
});

document.querySelector("#miniCart").addEventListener("keydown", function (e) {
    if (e.key !== "Escape") {
        return;
    }
    if (document.querySelector(".miniCartBodyItemsItemInfoWarrantyPopover.active")) {
        e.preventDefault();
        e.stopPropagation();
        closeWarrantyPopovers();
    }
});

/**************
 Open / Close Events
 ***************/

// close event
document.querySelector("#miniCart").addEventListener("close", function () {
    document.querySelector("body").classList.remove("noScroll");

    // arm the A/B exposure latch for the next viewing. This lives on the dialog's native close
    // event, not in closeCart(), so Escape and the backdrop reset it too - <dialog> closes on
    // Escape without any of our code running. (The gate's own cache is what stops a second
    // viewing from enrolling the same shopper twice.)
    cartExposureDispatched = false;
});

// close cart function
function closeCart() {
    document.querySelector("#miniCart").close();
}

// open cart event listener
document.querySelectorAll(".openCart").forEach(function (element) {
    element.addEventListener("click", function (e) {
        e.preventDefault();
        openCart();
    });
});

// close cart event listener
document.querySelectorAll(".closeCart").forEach(function (element) {
    element.addEventListener("click", closeCart);
});

// close cart on backdrop click - using coords avoids misfires on the dialog's own padding
document.querySelector("#miniCart").addEventListener("click", function (e) {
    if (e.detail === 0) {
        return;
    }
    const rect = this.getBoundingClientRect();
    const insideDialog = e.clientX >= rect.left && e.clientX <= rect.right && e.clientY >= rect.top && e.clientY <= rect.bottom;
    if (!insideDialog) {
        closeCart();
    }
});

// checkout button
document.querySelector("#miniCartFooterCheckout").addEventListener("click", function () {
    this.classList.add("loading");
    window.location.href = `${Shopify.routes.root}checkout`;
});

/**************
 Upsells Carousel
 ***************/

// horizontal free-scroll swiper for the upsell row
const upsellsScroll = document.querySelector("#miniCartBodyUpsellsScroll");
if (upsellsScroll) {
    new Swiper(upsellsScroll, {
        slidesPerView: "auto",
        spaceBetween: 10,
        freeMode: true,
        grabCursor: true,
        mousewheel: {
            forceToAxis: true
        },
        observer: true,
        observeParents: true
    });
}

/**************
 Init
 ***************/

// warm the supplemental-data cache so the first open renders complete
if (cart.item_count > 0) {
    getCartData().then(data => cartData = data);
}

// check is coming from the cart
if (document.referrer.includes("/cart")) {
    openCart();
}
